import React, { FC, useState } from 'react';
import { useHistory } from 'react-router-dom';

import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import AcceptanceConfirmationModal from '../../../sharedModules/layout/AcceptanceConfirmationModal';
import DataTable from '../../../sharedModules/dataTable';
import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';
import InformationField from '../../../sharedModules/form/components/InformationField';
import LoadingWrapper from '../../../sharedModules/layout/LoadingWrapper';

import { BackflowJobItem } from '../../model';

import axios from '../../../sharedModules/utils/axios.utils';

interface FlaggedDevicesSubListProps {
  afterComplete: () => void;
  jobItemsSubList: BackflowJobItem[];
  title: string;
}

const FlaggedDevicesSubList: FC<FlaggedDevicesSubListProps> = (props) => {
  const history = useHistory();

  let { afterComplete, jobItemsSubList, title } = props;

  const [acceptanceConfirmationModalOpen, setAcceptanceConfirmationModalOpen] = useState<boolean>(false);

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [deviceToComplete, setDeviceToComplete] = useState<BackflowJobItem>();
  const [loading, setLoading] = useState<boolean>(false);

  const markDeviceHidden = async () => {
    setLoading(true);
    try {
      await axios.put(`/backflows/backflow_job_items/${deviceToComplete.id}`, { processed: true });
      afterComplete();
      setErrorMessage('');
    } catch (error) {
      setErrorMessage('Something went wrong completing the device');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteClicked = (item) => {
    setAcceptanceConfirmationModalOpen(true);
    setDeviceToComplete(item);
  };

  const flaggedDeviceColumns = [
    { name: 'Company', selector: 'company_name' },
    { name: 'Logging Tech', selector: 'display_emp_id' },
    { name: 'System No', selector: 'system_no' },
    {
      name: 'Actions',
      cell: (row) => (
        <Button variant="contained" onClick={() => handleCompleteClicked(row)}>
          Complete
        </Button>
      ),
    },
  ];

  const deviceInfo = (
    <Grid container>
      <Grid item>
        <InformationField label="Company" value={deviceToComplete?.company_name} />
      </Grid>
      <Grid item>
        <InformationField label="Logging Tech" value={deviceToComplete?.display_emp_id} />
      </Grid>
      <Grid item>
        <InformationField label="System No" value={deviceToComplete?.system_no} />
      </Grid>
    </Grid>
  );

  return (
    <LoadingWrapper isLoading={loading}>
      <Grid container>
        <AcceptanceConfirmationModal
          onConfirm={markDeviceHidden}
          message={deviceInfo}
          modalOpen={acceptanceConfirmationModalOpen}
          setModalOpen={setAcceptanceConfirmationModalOpen}
          title={`Mark this ${title} Device Complete?`}
        />
        <Grid item xs={12}>
          <Typography variant="h5">{title} Devices</Typography>
        </Grid>
        <Grid item xs={12}>
          <ErrorMessage message={errorMessage} />
        </Grid>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <DataTable
                title=""
                columns={flaggedDeviceColumns}
                data={jobItemsSubList}
                onRowClicked={(row: any) =>
                  history.push(`/backflow_tests/backflow_form/${row.id}?backLink=/backflow_tests/flagged_devices`)
                }
                dense
                highlightOnHover
                pointerOnHover
                noHeader
                striped
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </LoadingWrapper>
  );
};

export default FlaggedDevicesSubList;
